import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Button, InputRef, Table, Tooltip } from 'antd'
import {
  CheckCircleOutlined,
  CloseSquareOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons'

import { path } from 'src/libs'
import { Guard } from 'src/features/session/guard'
import { getColumnSearchProps } from 'src/libs/tableFunctions'
import { ColumnsType } from 'antd/lib/table'
import { getNumberFormat } from 'src/libs/utils'

import { onChangeParticipantsList } from 'src/pages/goldenMay2026/participants/model'
import { useParticipantsList } from 'src/pages/goldenMay2026/participants/hooks'
import { BanModal } from 'src/pages/goldenMay2026/participants/widgets/banModal/component'
import { Participant } from '../types'

export const ParticipantsList = () => {
  const [modalData, setModalData] = useState<string | null>(null)
  const [searchText, setSearchText] = useState<{ [key: string]: string }>({})
  const searchInput = useRef<InputRef>(null)

  const { participants, loading, totalCountOfParticipants } =
    useParticipantsList()


  useEffect(() => {
    onChangeParticipantsList({
      searchText,
      page: 1,
      orderBy: 'allTickets',
      orderDirection: 'descend'
    })
  }, [searchText])

  const columns: ColumnsType<Participant> = [
    {
      title: 'ID',
      dataIndex: ['client', 'clientId'],
      defaultSortOrder: 'descend',
      ...getColumnSearchProps<Participant>(
        'client.clientId',
        setSearchText,
        searchText,
        searchInput,
      ),
      render: (id) => (
        <Link to={path.clients.details(id)} style={{ textDecoration: 'none' }}>
          {id}
        </Link>
      ),
    },
    {
      title: 'Name',
      dataIndex: ['client', 'fullName'],
      ...getColumnSearchProps<Participant>(
        'client.fullName',
        setSearchText,
        searchText,
        searchInput,
      ),
    },
    {
      title: 'Email',
      dataIndex: ['client', 'email'],
      ...getColumnSearchProps<Participant>(
        'client.email',
        setSearchText,
        searchText,
        searchInput,
      ),
    },
    {
      title: 'Is Partner',
      dataIndex: ['client', 'partner'],
      filters: [
        {
          text: 'Yes',
          value: true,
        },
        {
          text: 'No',
          value: false,
        },
      ],
      filterMultiple: false,
      render: (isPartner: boolean) => (
        <>
          {isPartner ? (
            <CheckCircleOutlined className={'text-success'} />
          ) : (
            <CloseSquareOutlined className={'text-danger'} />
          )}
        </>
      ),
    },
    {
      title: 'Tickets',
      dataIndex: 'allTickets',
      sorter: true,
      defaultSortOrder: 'descend',
      render: (item , record) => (
        <div>
          {item}{' '}
          <Tooltip
            title={
              <div>
                <div>Trading: {record.ticketsCount}</div>
                <div>New client: {record.clientsFirstTicketsCount}</div>
                <div>Client trading: {record.clientsOverallTicketsCount}</div>
              </div>
            }
          >
            <InfoCircleOutlined />
          </Tooltip>
        </div>
      ),
    },
    {
      title: 'Tasks',
      dataIndex: 'completedTasks',
      sorter: true,
    },
    {
      title: 'Lots',
      dataIndex: 'lots',
      sorter: true,
      render: (lots: string) => lots && getNumberFormat(lots, 2),
    },
    {
      title: 'Status',
      sorter: true,
      key: 'status',
      render: (item: Participant) => {
        return item.bannedAt ? item.banReason : item.status
      },
      filterMultiple: false,
      filters: [
        {
          text: 'active',
          value: 'active',
        },
        {
          text: 'ban',
          value: 'disqualified',
        },
      ],
    },
    {
      title: 'Actions',
      fixed: 'right',
      width: 150,
      render: (item: Participant) => {
        const isActive = !item.bannedAt
        return (
          <>
            {isActive ? (
              <Guard permission="golden_may_2026_ban">
                <Button
                  danger
                  type="primary"
                  onClick={() => setModalData(String(item.client.clientId))}
                >
                  Disqualification
                </Button>
              </Guard>
            ) : null}
          </>
        )
      },
    },
  ]

  return (
    <>
      <Table
        dataSource={participants}
        bordered={true}
        className="overflow-auto"
        loading={loading}
        rowKey={(record) => record.client.clientId}
        columns={columns}
        pagination={{
          showSizeChanger: true,
          total: totalCountOfParticipants,
        }}
        onChange={(pagination, filters, sorter: any) => {
          onChangeParticipantsList({
            page: pagination.current,
            limit: pagination.pageSize,
            orderBy: sorter?.field || sorter?.columnKey,
            orderDirection: sorter?.order,
            filters,
          })
        }}
      />
      {modalData && (
        <BanModal
          id={modalData}
          isVisible={Boolean(modalData)}
          onClose={() => setModalData(null)}
        />
      )}
    </>
  )
}
